"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DonutChart = DonutChart;
var _lib = require("@a1s/lib");
var _group = require("@visx/group");
var _Pie = _interopRequireDefault(require("@visx/shape/lib/shapes/Pie"));
var _react = _interopRequireWildcard(require("react"));
var _base = require("../../base");
var _components = require("../../components");
var _animations = require("../../shared/animations");
var _styled = require("./styled");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
//
// Main component
// -------------------------------------------------------------------------------------------------

/**
 *
 * @alpha
 */
function DonutChart({
  children,
  css,
  height,
  testId,
  thickness = 'thin',
  width
}) {
  var _React$Children$map;
  // Accept numbers or strings for the dimension props
  height = (0, _lib.num)(height);
  width = (0, _lib.num)(width);
  const groupTypes = [];

  // Isolate the the `Caption` element that was passed in.
  const [caption] = (_React$Children$map = _react.default.Children.map(children, child => {
    if ( /*#__PURE__*/(0, _react.isValidElement)(child) && child.type === Caption) return child;
    return null;
  })) === null || _React$Children$map === void 0 ? void 0 : _React$Children$map.filter(Boolean);

  // Isolate all of the `Segment` elements from the children
  const segments = _react.default.Children.toArray(children).filter(child => {
    if (! /*#__PURE__*/(0, _react.isValidElement)(child)) return false;
    const {
      type
    } = child;
    return type === Segment || type === Group;
  }).map(child => {
    const {
      props: {
        children
      }
    } = child;
    const elements = children ? children : [child];
    return elements.map((child, index) => {
      const rounded = roundChildren(elements),
        total = rounded.reduce((a, b) => a + b);
      if (elements.length == 1) {
        groupTypes.push({
          total,
          type: 'orphan',
          value: rounded[index]
        });
      } else if (!elements[index - 1]) {
        groupTypes.push({
          total,
          type: 'first',
          value: rounded[index]
        });
      } else if (!elements[index + 1]) {
        groupTypes.push({
          total,
          type: 'last',
          value: rounded[index]
        });
      } else {
        groupTypes.push({
          total,
          type: 'center',
          value: rounded[index]
        });
      }
      return child;
    });
  }).flat().filter(Boolean);
  const outerRadius = height / 2;
  const innerRadius = thickness === 'thin' ? outerRadius - 4 : outerRadius - 16;
  function roundValue(value) {
    return typeof value === 'string' ? Number.parseInt(value, 10) : value || 0;
  }
  function roundChildren(children) {
    return _react.default.Children.map(children, child => {
      const {
        value
      } = child.props;
      return roundValue(value);
    });
  }

  // Round up all of the data from the segments
  const data = roundChildren(segments).map((value, index) => {
    return {
      value,
      index
    };
  });
  return /*#__PURE__*/_react.default.createElement(_styled.StyledBase, {
    "data-testid": testId
  }, /*#__PURE__*/_react.default.createElement(_styled.StyledSVG, {
    css: css,
    role: "img",
    viewBox: `0 0 ${width} ${height}`
  }, /*#__PURE__*/_react.default.createElement(_group.Group, {
    left: width / 2,
    top: height / 2
  }, /*#__PURE__*/_react.default.createElement(_Pie.default, {
    data: data,
    pieValue: p => p.value,
    innerRadius: innerRadius,
    outerRadius: outerRadius,
    pieSort: (a, b) => {
      return a.index - b.index;
    }
  }, ({
    arcs,
    path
  }) => {
    return arcs.map((arc, i) => {
      const child = Array.isArray(segments) ? segments[i] : null;
      if (! /*#__PURE__*/(0, _react.isValidElement)(child)) return null;
      const {
        total,
        type,
        value
      } = groupTypes[i];
      const gap = 0.02;
      const getGap = (value, total) => {
        const groupValuePercentage = 100 * value / total;
        return gap / 100 * groupValuePercentage;
      };
      switch (type) {
        case 'last':
          arc.startAngle += getGap(value, total);
          break;
        case 'center':
          arc.startAngle += getGap(value, total);
          arc.endAngle += getGap(groupTypes[i + 1].value, groupTypes[i + 1].total);
          break;
        case 'orphan':
          arc.startAngle += gap;
          break;
        case 'first':
          arc.startAngle += gap;
          arc.endAngle += getGap(groupTypes[i + 1].value, groupTypes[i + 1].total);
          break;
      }
      return /*#__PURE__*/(0, _react.cloneElement)(child, {
        d: path(arc),
        key: i
      });
    });
  }))), caption);
}

//
// Sub-components
// -------------------------------------------------------------------------------------------------

DonutChart.Caption = Caption;
DonutChart.Group = Group;
DonutChart.Segment = Segment;
function Group({
  children
}) {
  return /*#__PURE__*/_react.default.createElement("g", null, children);
}
function Caption({
  children,
  testId
}) {
  return /*#__PURE__*/_react.default.createElement(_styled.StyledCaption, {
    "data-testid": testId
  }, children);
}
function Segment({
  color,
  testId,
  value = 0,
  ...props
}) {
  return /*#__PURE__*/_react.default.createElement(_styled.StyledPath, _extends({
    "data-testid": testId,
    "data-value": value,
    fill: color
  }, props));
}

//
// Utility components
// -------------------------------------------------------------------------------------------------

DonutChart.Loadable = Loadable;
function Loadable({
  height,
  loading,
  width,
  testId,
  ...props
}) {
  const isLoading = (0, _components.useLoadable)(loading);
  if (!isLoading) return /*#__PURE__*/_react.default.createElement(DonutChart, _extends({
    height: height,
    testId: testId,
    width: width
  }, props));
  return /*#__PURE__*/_react.default.createElement(_base.Box, {
    bg: "$gray300",
    css: {
      animation: `${_animations.pulse} 1.5s linear infinite`,
      height: `${height}px`,
      margin: '0 auto',
      width: `${width}px`
    },
    "data-testid": testId,
    r: true
  });
}